import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Wizard from '../containers/Modal/Wizard';
import { Input, SelectField } from '../containers/Form';
import Strings from '../Strings';
import { closeModal } from '../actions/modal';
import { actions as orgActions } from '../pages/Organizations/redux/actions';
import { notificationActions } from '../components/Notification/redux/actions';
import { UPDATE_ORG_TEMPLATE_ERROR } from '../pages/Organizations/redux/constants';
import { isSuperUser } from '../utils/userRoles';

export const EDIT_ORG_TEMPLATE_MODAL_NAME = 'edit-organization-template';

function EditOrganizationTemplateModal(props) {
  const {
    data,
    onCancel,
    getOrganizations,
    updateOrgTemplateEdit,
    getOrgTemplates,
    showNotification,
  } = props;

  const template = data?.template || {};
  const isSuperUserRole = isSuperUser();
  const [name, setName] = React.useState(template?.name || '');
  const [ownerUuid, setOwnerUuid] = React.useState(template?.owner_uuid || template?.owner?.uuid || '');
  const [organizations, setOrganizations] = React.useState([]);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setName(template?.name || '');
    setOwnerUuid(template?.owner_uuid || template?.owner?.uuid || '');
  }, [template?.name, template?.owner_uuid, template?.owner?.uuid]);

  React.useEffect(() => {
    getOrganizations({ limit: 1000, offset: 0 }).then(resp => {
      const raw = Array.isArray(resp?.response)
        ? resp.response
        : resp?.response?.data || [];
      const options = raw.map(org => {
        const value = org?.uuid || org?.masked_id || org?.id;
        const label = org?.name || String(value || '');
        return { value, label };
      }).filter(option => option.value);

      if (!ownerUuid && template?.owner) {
        const matched = options.find(option => option.label === template.owner);
        if (matched) {
          setOwnerUuid(matched.value);
        }
      }

      setOrganizations(options);
    });
  }, [getOrganizations, template?.owner, template?.uuid]);

  React.useEffect(() => {
    const errors = [];
    if (!name.trim()) {
      errors.push({ property: 'name', propertyName: Strings.name, missing: true });
    }
    if (!isSuperUserRole && !ownerUuid) {
      errors.push({ property: 'owner', propertyName: Strings.owner, missing: true });
    }
    setValidationErrors(errors);
  }, [name, ownerUuid, isSuperUserRole]);

  const onSubmit = () => {
    if (isSaving) {
      return;
    }
    if (!template?.uuid) {
      showNotification(Strings.errors?.unknownError || Strings.noData, 5000, true);
      return;
    }

    setIsSaving(true);
    const payload = {
      name: name.trim(),
      owner_uuid: ownerUuid || null,
    };

    updateOrgTemplateEdit(payload, { organizationId: template.uuid }).then(resp => {
      if (resp?.type === UPDATE_ORG_TEMPLATE_ERROR) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
        setIsSaving(false);
        return;
      }
      showNotification(Strings.templateSaved);
      getOrgTemplates();
      onCancel();
    });
  };

  const ownerOptions = [{ value: '', label: Strings.none }].concat(organizations);

  const pages = [
    {
      id: 'edit-org-template-page',
      title: Strings.editTemplate,
      content: (
        <>
          <Input
            name="templateName"
            id="templateName"
            label={Strings.name}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'name')?.errors || []}
            data-testid="template-name"
          />
          <SelectField
            name="templateOwner"
            id="templateOwner"
            label={Strings.owner}
            value={ownerUuid}
            onChange={option => setOwnerUuid(option?.value || '')}
            data={ownerOptions}
            isSearchable
            searchByLabelOnly
            isRequired={!isSuperUserRole}
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'owner')?.errors || []}
            data-testid="template-owner"
          />
        </>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !isSaving,
      nextButton: { text: Strings.save },
    },
  ];

  return (
    <Wizard
      name={EDIT_ORG_TEMPLATE_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      showPagesFilter={false}
    />
  );
}

EditOrganizationTemplateModal.propTypes = {
  data: PropTypes.shape({
    template: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
      owner: PropTypes.string,
      owner_uuid: PropTypes.string,
    }),
  }),
  onCancel: PropTypes.func,
  getOrganizations: PropTypes.func,
  updateOrgTemplateEdit: PropTypes.func,
  getOrgTemplates: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(EDIT_ORG_TEMPLATE_MODAL_NAME)),
  getOrganizations: pageRequest => dispatch(orgActions.queryOrganizations(pageRequest)),
  updateOrgTemplateEdit: (data, pageRequest) => dispatch(orgActions.updateOrgTemplateEdit(data, pageRequest)),
  getOrgTemplates: () => dispatch(orgActions.getOrgTemplates()),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(EditOrganizationTemplateModal);
